import React, { useDeferredValue, useMemo } from 'react';
import { ConfigProvider, theme as antdTheme } from 'antd';
import './HarborPage.css';
import './HarborColors.css';
import { PALETTE, useAccent } from './HarborAccents';

// Pages given the full Harbor page styling (HarborPage.css) so far. Every page
// gets the Harbor colours while the Harbor look is on; only these also get the
// card, heading and table treatment, so pages can be redone one at a time.
export const HARBOR_PAGES = ['/agent-summary'];

// Off: an empty theme, which inherits the app's (no theme at all builds a different tree).
const NO_THEME = {};

// Status colours are the same in every accent; the rest come from the chosen accent (HarborAccents.js).
const STATUS = {
  dark: { success: '#33d75c', warning: '#d99a3e', error: '#ea4a2a' },
  light: { success: '#39a257', warning: '#b07a1f', error: '#e63f1e' },
};

// The solid colour a translucent rgba() shows as over a solid hex background.
const over = (rgba, hex) => {
  const [r, g, b, a] = rgba.match(/[\d.]+/g).map(Number);
  const bg = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `#${[r, g, b].map((c, i) => Math.round(c * a + bg[i] * (1 - a)).toString(16).padStart(2, '0')).join('')}`;
};

// Ant Design's theme, Harbor edition: the accent colour, matching ink, soft borders, and
// status colours in the prototype's softer tones. While the Harbor look is on
// it wraps the whole layout (nav, pages, pop-ups); it nests inside the app's
// ConfigProvider, so it only overrides what it names. With on={false} (the classic look) it
// passes the app's theme through, keeping the same tree so switching looks doesn't remount pages.
export default function HarborTheme({ dark, on = true, children }) {
  // Deferred: a new accent recolours the nav and the --hb-* colours at once, and Ant Design
  // re-renders the page in the background (seconds on a big page such as Leaderboard).
  const accent = useDeferredValue(useAccent());
  // Memoised: a new theme object on every render makes Ant Design recompute the
  // styles of every component on the page.
  const theme = useMemo(() => {
    const mode = dark ? 'dark' : 'light';
    const p = { ...PALETTE[accent][mode], ...STATUS[mode] };
    return {
      algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        colorPrimary: p.brass,
        colorInfo: p.brass,
        colorSuccess: p.success,
        colorWarning: p.warning,
        colorError: p.error,
        colorLink: p.brassHi,
        colorLinkHover: p.brass,
        borderRadius: 8,
        colorBgContainer: p.surface,
        colorBgElevated: p.elevated,
        colorBorder: p.border,
        colorBorderSecondary: p.borderSoft,
        colorText: p.ink,
        colorTextHeading: p.ink,
        colorTextSecondary: p.inkDim,
        colorTextTertiary: p.inkFaint,
        colorTextDescription: p.inkDim,
      },
      components: {
        Card: { headerBg: 'transparent' },
        Table: {
          // Solid, not transparent: frozen (fixed) columns cover the cells scrolling under
          // them with these backgrounds, so see-through ones let the header and the hovered
          // row show through. Same look: the surface, and the hover tint already mixed in.
          headerBg: p.surface, headerColor: p.brassHi, headerSplitColor: 'transparent',
          rowHoverBg: over(p.hover, p.surface), borderColor: p.borderSoft,
        },
        Tabs: { itemColor: p.inkDim, itemHoverColor: p.ink, itemSelectedColor: p.brassHi, inkBarColor: p.brass },
        Segmented: { trackBg: p.track, itemSelectedBg: p.brass, itemSelectedColor: p.onBrass, itemHoverBg: p.trackHover, itemColor: p.inkDim },
      },
    };
  }, [dark, accent]);
  return <ConfigProvider theme={on ? theme : NO_THEME}>{children}</ConfigProvider>;
}
